import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X, Hammer } from "lucide-react";

const LINKS = [
  { label: "Services", href: "#services" },
  { label: "Work", href: "#work" },
  { label: "Process", href: "#process" },
  { label: "Why TCB", href: "#why-tcb" },
];

export const PHONE_DISPLAY = "(361) 438-8172";
export const PHONE_TEL = "tel:+13614388172";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-nav"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b ${
        scrolled ? "bg-ink/90 backdrop-blur-md border-line" : "bg-gradient-to-b from-ink/80 to-transparent border-transparent"
      }`}
    >
      <div className="hazard-stripes h-[3px] w-full" aria-hidden="true" />
      <div className="max-w-[1440px] mx-auto px-4 sm:px-8 lg:px-16 h-16 md:h-20 flex items-center justify-between">
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-3 group">
          <span className="w-9 h-9 bg-safety clip-notch-sm flex items-center justify-center transition-transform duration-300 group-hover:-translate-y-0.5">
            <Hammer className="w-5 h-5 text-ink" strokeWidth={2.5} />
          </span>
          <span className="font-display text-xl md:text-2xl uppercase tracking-wide text-bone">
            TCB <span className="text-safety">Properties</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(" ", "-")}`}
              className="font-mono text-[11px] uppercase tracking-[0.2em] text-steel hover:text-bone transition-colors duration-200"
            >
              {l.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={PHONE_TEL}
            data-testid="nav-call-btn"
            className="hidden sm:flex items-center gap-2 bg-safety hover:bg-safety-hover text-ink font-bold text-sm px-5 py-2.5 clip-notch-sm transition-all duration-200 hover:-translate-y-0.5"
          >
            <Phone className="w-4 h-4" strokeWidth={2.5} />
            {PHONE_DISPLAY}
          </a>
          <button
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
            className="md:hidden w-10 h-10 flex items-center justify-center border border-line text-bone"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="md:hidden overflow-hidden bg-ink/95 backdrop-blur-md border-b border-line"
            data-testid="nav-mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-2xl uppercase text-bone hover:text-safety transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={PHONE_TEL}
                className="flex items-center justify-center gap-2 bg-safety text-ink font-bold px-5 py-3.5 clip-notch-sm"
              >
                <Phone className="w-4 h-4" strokeWidth={2.5} /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
